import { scopedApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import { DBExportFormat, DBRecurringExport, DBRecurringExportProfileInput, DBRecurringExportSourceInput, DBExportType } from '@/types/export';
import { EndpointResponse } from '@/types/endpoints';
import { utils } from '../../utils';
import type { WriteRequestOptions } from '../requestOptions';

const validate = (
  name: string,
  exportType: string,
  source: DBRecurringExportSourceInput,
  exportProfileId: number | undefined,
  exportProfile: DBRecurringExportProfileInput | undefined,
  query: string | undefined
) => {
  if (!name) {
    throw new Error('Name is required');
  }
  if (!exportType) {
    throw new Error('Export type is required');
  }
  if (!source) {
    throw new Error('Source is required');
  }
  if (!exportProfileId && !exportProfile) {
    throw new Error('Export profile is required');
  }
};

type RequestParams = {
  name: string;
  exportType: DBExportType;
  format: DBExportFormat;
  source: DBRecurringExportSourceInput;
  exportProfileId?: number;
  exportProfile?: DBRecurringExportProfileInput;
  query?: string;
};

type Response = DBRecurringExport;

export const createRecurringExport = async (
  scope: RequestScope,
  { name, exportType, format, source, exportProfileId, exportProfile, query }: RequestParams,
  options: WriteRequestOptions
): Promise<EndpointResponse<Response>> => {
  try {
    validate(name, exportType, source, exportProfileId, exportProfile, query);

    const response = await scopedApi(scope).post<EndpointResponse<Response>>(
      '/recurring_export',
      {
        name,
        export_type: exportType,
        format,
        source: {
          type: source.type,
          host: source.host,
          port: source.port,
          username: source.username,
          password: source.password,
          frequency: source.frequency,
          file_template: source.fileTemplate,
          ...(source.dateTimeTemplateVar !== undefined && { date_time_template_var: source.dateTimeTemplateVar }),
        },
        ...(exportProfileId !== undefined && { export_profile_id: exportProfileId }),
        ...(exportProfile !== undefined && { export_profile: exportProfile }),
        ...(query !== undefined && { query }),
      },
      options
    );

    return response.data;
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
